import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import api from "../../services/api";

import { refreshCurrentAuthUser } from "../../services/authService";

import styles from "../../css/shared/Login.module.css";

import logo from "../../assets/image1.png";

import {
  applyAuthLoginPayload,
  clearLegacyAuthStorage,
} from "../../utils/auth";

/* =========================================================
   PASSWORD VISIBILITY ICON
========================================================= */

function EyeIcon({ off = false }) {
  if (off) {
    return (
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="
            M3 3l18 18
            M10.58 10.58A2 2 0 0012 14
            a2 2 0 001.42-.58
            M9.88 5.09A10.94 10.94 0 0112 5
            c5 0 9.27 3.11 11 7
            -0.61 1.37-1.54 2.63-2.72 3.71
            M6.1 6.1C3.91 7.43 2.18 9.47 1 12
            c1.73 3.89 6 7 11 7
            1.55 0 3.03-.3 4.38-.84
          "
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="
          M1 12
          c1.73-3.89 6-7 11-7
          s9.27 3.11 11 7
          c-1.73 3.89-6 7-11 7
          S2.73 15.89 1 12z
        "
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

/* =========================================================
   LOGIN COMPONENT
========================================================= */

export default function Login() {
  const navigate = useNavigate();

  /* =======================================================
     LOGIN STATES
  ======================================================= */

  const [empId, setEmpId] = useState("");

  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const [loginError, setLoginError] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [capsLockLogin, setCapsLockLogin] = useState(false);

  /* =======================================================
     FORGOT PASSWORD STATES
  ======================================================= */

  const [open, setOpen] = useState(false);

  const [fpEmpId, setFpEmpId] = useState("");

  const [email, setEmail] = useState("");

  const [otp, setOtp] = useState("");

  const [step, setStep] = useState(1);

  const [newPassword, setNewPassword] = useState("");

  const [confirmPassword, setConfirmPassword] = useState("");

  const [otpLoading, setOtpLoading] = useState(false);

  const [timer, setTimer] = useState(0);

  const [showNewPw, setShowNewPw] = useState(false);

  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const [capsLockNewPw, setCapsLockNewPw] = useState(false);

  const [capsLockConfirmPw, setCapsLockConfirmPw] = useState(false);

  const [forgotPasswordError, setForgotPasswordError] = useState("");

  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");

  /* =======================================================
     CLEAR OLD LOGIN SESSION
  ======================================================= */

  useEffect(() => {
    clearLegacyAuthStorage();

    sessionStorage.removeItem("tempLoginToken");

    sessionStorage.removeItem("tempEmpId");
  }, []);

  /* =======================================================
     OTP TIMER
  ======================================================= */

  useEffect(() => {
    if (timer <= 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTimer((current) => Math.max(0, current - 1));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [timer]);

  /* =======================================================
     CAPS LOCK
  ======================================================= */

  const handleCapsLock = (setter) => (event) => {
    setter(event.getModifierState("CapsLock"));
  };

  /* =======================================================
     LOGIN INPUT CHANGE
  ======================================================= */

  const handleEmpIdChange = (event) => {
    setEmpId(event.target.value);

    if (loginError) {
      setLoginError("");
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);

    if (loginError) {
      setLoginError("");
    }
  };

  /* =======================================================
     LOGIN
  ======================================================= */

  const login = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading) {
      return;
    }

    const normalizedEmpId = String(empId || "").trim();

    /*
     * Do not trim password.
     *
     * Passwords may legally contain spaces.
     */
    const enteredPassword = typeof password === "string" ? password : "";

    /* =====================================================
       LOGIN VALIDATION
    ===================================================== */

    if (!normalizedEmpId && !enteredPassword) {
      setLoginError("Employee ID and Password are required.");

      return;
    }

    if (!normalizedEmpId) {
      setLoginError("Employee ID is required.");

      return;
    }

    if (!enteredPassword) {
      setLoginError("Password is required.");

      return;
    }

    setLoginError("");
    setLoading(true);

    try {
      /* ===================================================
         LOGIN API
      =================================================== */

      const response = await api.post(
        "/auth/login",
        {
          empId: normalizedEmpId,

          password: enteredPassword,
        },
        {
          /*
           * IMPORTANT:
           *
           * services/api.js should respect this
           * property and must not redirect the
           * browser when login itself returns 401.
           */
          skipAuthRedirect: true,
        },
      );

      const loginData = response?.data || {};

      /* ===================================================
         TWO FACTOR LOGIN
      =================================================== */

      if (loginData.requires2FA) {
        if (!loginData.tempToken) {
          setLoginError(
            "Unable to start two-factor authentication. Please try again.",
          );

          return;
        }

        sessionStorage.setItem("tempLoginToken", loginData.tempToken);

        sessionStorage.setItem("tempEmpId", loginData.empId || normalizedEmpId);

        navigate("/two-factor", {
          replace: true,
        });

        return;
      }

      /* ===================================================
         VALIDATE LOGIN RESPONSE
      =================================================== */

      if (!loginData.token) {
        console.error(
          "Login response does not contain authentication token:",
          loginData,
        );

        setLoginError("Unable to complete login. Please try again.");

        return;
      }

      /* ===================================================
         SAVE AUTH SESSION
      =================================================== */

      applyAuthLoginPayload(loginData);

      /* ===================================================
         LOAD FULL CURRENT USER
      =================================================== */

      await refreshCurrentAuthUser({
        skipAuthRedirect: true,
      }).catch((error) => {
        console.error("Refresh auth user after login error:", error);
      });

      setLoginError("");

      /* ===================================================
         ROLE REDIRECTION
      =================================================== */

      const normalizedRole = String(loginData.role || "")
        .trim()
        .toUpperCase();

      const destination =
        normalizedRole === "ADMIN" || normalizedRole === "SUPER_ADMIN"
          ? "/create"
          : "/active";

      navigate(destination, {
        replace: true,
      });
    } catch (error) {
      const status = Number(error?.response?.status || 0);

      const backendMessage = String(
        error?.response?.data?.message || "",
      ).trim();

      /* ===================================================
         WRONG EMPLOYEE ID / PASSWORD
      =================================================== */

      if (status === 401) {
        setLoginError("Employee ID or Password is incorrect.");

        return;
      }

      /* ===================================================
         ACCOUNT INACTIVE
      =================================================== */

      if (status === 403) {
        setLoginError(
          backendMessage ||
            "Your account is inactive. Please contact your administrator.",
        );

        return;
      }

      /* ===================================================
         VALIDATION ERROR
      =================================================== */

      if (status === 400) {
        setLoginError(
          backendMessage || "Please enter a valid Employee ID and Password.",
        );

        return;
      }

      /* ===================================================
         TOO MANY REQUESTS
      =================================================== */

      if (status === 429) {
        setLoginError(
          backendMessage ||
            "Too many login attempts. Please wait and try again.",
        );

        return;
      }

      /* ===================================================
         NETWORK ERROR
      =================================================== */

      if (!error?.response) {
        setLoginError(
          "Unable to connect to the server. Please check your network connection and try again.",
        );

        return;
      }

      /* ===================================================
         SERVER ERROR
      =================================================== */

      console.error("Login request error:", error);

      setLoginError(
        status >= 500
          ? "Server is temporarily unavailable. Please try again."
          : backendMessage || "Unable to sign in. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     FORGOT PASSWORD MODAL RESET
  ======================================================= */

  const handleClose = () => {
    if (otpLoading) {
      return;
    }

    setOpen(false);

    setStep(1);

    setFpEmpId("");

    setEmail("");

    setOtp("");

    setNewPassword("");

    setConfirmPassword("");

    setTimer(0);

    setShowNewPw(false);

    setShowConfirmPw(false);

    setCapsLockNewPw(false);

    setCapsLockConfirmPw(false);

    setForgotPasswordError("");

    setForgotPasswordSuccess("");
  };

  /* =======================================================
     OPEN FORGOT PASSWORD
  ======================================================= */

  const handleOpenForgotPassword = () => {
    setLoginError("");

    setForgotPasswordError("");

    setForgotPasswordSuccess("");

    setOpen(true);
  };

  /* =======================================================
     PASSWORD STRENGTH
  ======================================================= */

  const getPasswordStrength = (value) => {
    if (!value) {
      return "";
    }

    let strength = 0;

    if (value.length >= 6) {
      strength += 1;
    }

    if (/[A-Z]/.test(value)) {
      strength += 1;
    }

    if (/[0-9]/.test(value)) {
      strength += 1;
    }

    if (/[@$!%*?&]/.test(value)) {
      strength += 1;
    }

    if (strength <= 1) {
      return "Weak";
    }

    if (strength <= 3) {
      return "Medium";
    }

    return "Strong";
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const strengthColor =
    passwordStrength === "Strong"
      ? "#22c55e"
      : passwordStrength === "Medium"
        ? "#f59e0b"
        : "#f43f5e";

  const strengthWidth =
    passwordStrength === "Strong"
      ? "100%"
      : passwordStrength === "Medium"
        ? "60%"
        : newPassword
          ? "28%"
          : "0%";

  const isPasswordValid =
    newPassword.length >= 6 &&
    passwordStrength !== "Weak" &&
    newPassword === confirmPassword;

  /* =======================================================
     FORGOT PASSWORD - SEND OTP
  ======================================================= */

  const handleSendOtp = async () => {
    if (otpLoading) {
      return;
    }

    const normalizedEmpId = String(fpEmpId || "").trim();

    const normalizedEmail = String(email || "").trim();

    if (!normalizedEmpId || !normalizedEmail) {
      setForgotPasswordError("Employee ID and Email Address are required.");

      return;
    }

    setForgotPasswordError("");

    setForgotPasswordSuccess("");

    setOtpLoading(true);

    try {
      const response = await api.post(
        "/auth/forgot-password",
        {
          empId: normalizedEmpId,

          email: normalizedEmail,
        },
        {
          skipAuthRedirect: true,
        },
      );

      setFpEmpId(normalizedEmpId);

      setEmail(normalizedEmail);

      setStep(2);

      setTimer(60);

      setForgotPasswordSuccess(
        response?.data?.message ||
          "If the provided details are correct, an OTP has been sent.",
      );
    } catch (error) {
      setForgotPasswordError(
        error?.response?.data?.message ||
          "Unable to send OTP. Please try again.",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  /* =======================================================
     FORGOT PASSWORD - VERIFY OTP
  ======================================================= */

  const handleVerifyOtp = async () => {
    if (otpLoading) {
      return;
    }

    const normalizedOtp = String(otp || "").trim();

    if (!normalizedOtp) {
      setForgotPasswordError("OTP is required.");

      return;
    }

    setForgotPasswordError("");

    setForgotPasswordSuccess("");

    setOtpLoading(true);

    try {
      const response = await api.post(
        "/auth/verify-otp",
        {
          empId: fpEmpId,

          email,

          otp: normalizedOtp,
        },
        {
          skipAuthRedirect: true,
        },
      );

      setOtp(normalizedOtp);

      setStep(3);

      setForgotPasswordSuccess(
        response?.data?.message || "OTP verified successfully.",
      );
    } catch (error) {
      setForgotPasswordError(
        error?.response?.data?.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  /* =======================================================
     FORGOT PASSWORD - RESEND OTP
  ======================================================= */

  const handleResendOtp = async () => {
    if (otpLoading || timer > 0) {
      return;
    }

    setForgotPasswordError("");

    setForgotPasswordSuccess("");

    setOtpLoading(true);

    try {
      const response = await api.post(
        "/auth/forgot-password",
        {
          empId: fpEmpId,

          email,
        },
        {
          skipAuthRedirect: true,
        },
      );

      setOtp("");

      setTimer(60);

      setForgotPasswordSuccess(
        response?.data?.message || "OTP sent successfully.",
      );
    } catch (error) {
      setForgotPasswordError(
        error?.response?.data?.message ||
          "Unable to resend OTP. Please try again.",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  /* =======================================================
     FORGOT PASSWORD - RESET PASSWORD
  ======================================================= */

  const handleResetPassword = async () => {
    if (otpLoading || !isPasswordValid) {
      return;
    }

    setForgotPasswordError("");

    setForgotPasswordSuccess("");

    setOtpLoading(true);

    try {
      await api.post(
        "/auth/reset-password",
        {
          empId: fpEmpId,

          email,

          password: newPassword,
        },
        {
          skipAuthRedirect: true,
        },
      );

      /*
       * Preserve employee ID on login page
       * after successful password reset.
       */
      setEmpId(fpEmpId);

      setPassword("");

      setOpen(false);

      setStep(1);

      setFpEmpId("");

      setEmail("");

      setOtp("");

      setNewPassword("");

      setConfirmPassword("");

      setTimer(0);

      setShowNewPw(false);

      setShowConfirmPw(false);

      setCapsLockNewPw(false);

      setCapsLockConfirmPw(false);

      setForgotPasswordError("");

      setForgotPasswordSuccess("");

      setLoginError("");
    } catch (error) {
      setForgotPasswordError(
        error?.response?.data?.message ||
          "Unable to reset password. Please try again.",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  /* =======================================================
     STEP INDICATOR
  ======================================================= */

  const dotClass = (number) =>
    number < step
      ? `${styles.stepDot} ${styles.stepDotDone}`
      : number === step
        ? `${styles.stepDot} ${styles.stepDotActive}`
        : styles.stepDot;

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className={styles.loginRoot}>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          {/* =================================================
              LEFT - LOGIN FORM
          ================================================= */}

          <div className={styles.leftSection}>
            <Box
              component="form"
              onSubmit={login}
              className={styles.card}
              noValidate
            >
              {/* =============================================
                  HEADER
              ============================================= */}

              <div className={styles.header}>
                <Typography className={styles.title}>Welcome Back</Typography>

                <Typography className={styles.subtitle}>
                  Sign in to your INFONET account
                </Typography>
              </div>

              {/* =============================================
                  EMPLOYEE ID
              ============================================= */}

              <TextField
                label="Employee ID"
                value={empId}
                onChange={handleEmpIdChange}
                className={styles.input}
                fullWidth
                variant="outlined"
                autoComplete="username"
                autoFocus
                disabled={loading}
                inputProps={{
                  autoCapitalize: "none",

                  autoCorrect: "off",

                  spellCheck: false,
                }}
              />

              {/* =============================================
                  PASSWORD
              ============================================= */}

              <TextField
                type={showPassword ? "text" : "password"}
                label="Password"
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={handleCapsLock(setCapsLockLogin)}
                onKeyUp={handleCapsLock(setCapsLockLogin)}
                onBlur={() => setCapsLockLogin(false)}
                className={styles.input}
                fullWidth
                variant="outlined"
                autoComplete="current-password"
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        edge="end"
                        size="small"
                        className={styles.passwordToggle}
                        disabled={loading}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        <EyeIcon off={showPassword} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* =============================================
                  CAPS LOCK
              ============================================= */}

              {capsLockLogin && (
                <Typography className={styles.capsWarning}>
                  Caps Lock is on
                </Typography>
              )}

              {/* =============================================
                  LOGIN ERROR
              ============================================= */}

              {loginError && (
                <Alert
                  severity="error"
                  variant="outlined"
                  role="alert"
                  aria-live="polite"
                  sx={{
                    width: "100%",

                    boxSizing: "border-box",

                    mt: 0.5,

                    mb: 1,

                    borderRadius: "10px",

                    fontSize: "13px",

                    fontWeight: 600,

                    alignItems: "center",

                    "& .MuiAlert-message": {
                      width: "100%",
                    },
                  }}
                >
                  {loginError}
                </Alert>
              )}

              {/* =============================================
                  FORGOT PASSWORD
              ============================================= */}

              <Typography
                component="button"
                type="button"
                className={styles.forgotLink}
                onClick={handleOpenForgotPassword}
                sx={{
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  font: "inherit",
                }}
              >
                Forgot password?
              </Typography>

              {/* =============================================
                  LOGIN BUTTON
              ============================================= */}

              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                className={styles.button}
                fullWidth
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={18}
                      color="inherit"
                      sx={{
                        mr: 1,
                      }}
                    />
                    Signing in…
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>

              <Typography className={styles.footerText}>
                Need help? Contact your administrator
              </Typography>
            </Box>
          </div>

          {/* =================================================
              RIGHT - BRANDING
          ================================================= */}

          <div className={styles.rightSection}>
            <div
              className={styles.bubble}
              style={{
                width: 80,
                height: 80,
                top: "8%",
                right: "12%",
              }}
            />

            <div
              className={styles.bubble}
              style={{
                width: 44,
                height: 44,
                top: "22%",
                right: "5%",
              }}
            />

            <div
              className={styles.bubble}
              style={{
                width: 28,
                height: 28,
                bottom: "18%",
                right: "18%",
              }}
            />

            <div
              className={styles.bubble}
              style={{
                width: 60,
                height: 60,
                bottom: "10%",
                left: "16%",
              }}
            />

            <div className={styles.rightContent}>
              <img src={logo} alt="INFONET Logo" className={styles.logo} />

              <Typography className={styles.brandText}>
                Welcome to <span>INFONET</span>
              </Typography>

              <Typography className={styles.tagline}>
                Empowering Innovation &nbsp;•&nbsp; Connecting Excellence
              </Typography>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          FORGOT PASSWORD MODAL
      ===================================================== */}

      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" && otpLoading) {
            return;
          }

          handleClose();
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: styles.fpPaper,
        }}
      >
        {/* ===================================================
            MODAL TITLE
        =================================================== */}

        <DialogTitle className={styles.fpTitle} disableTypography>
          <Typography className={styles.fpTitleText}>
            {step === 1
              ? "Reset Password"
              : step === 2
                ? "Verify OTP"
                : "Set New Password"}
          </Typography>

          <Typography className={styles.fpSubtitle}>
            {step === 1
              ? "Enter your Employee ID and registered email"
              : step === 2
                ? "Enter the OTP sent to your email"
                : "Choose a strong new password"}
          </Typography>
        </DialogTitle>

        <DialogContent className={styles.fpContent}>
          {/* =================================================
              STEP INDICATOR
          ================================================= */}

          <div className={styles.stepDots}>
            <div className={dotClass(1)} />

            <div className={dotClass(2)} />

            <div className={dotClass(3)} />
          </div>

          {/* =================================================
              FORGOT PASSWORD ERROR
          ================================================= */}

          {forgotPasswordError && (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                mb: 2,

                borderRadius: "10px",

                fontSize: "12px",
              }}
            >
              {forgotPasswordError}
            </Alert>
          )}

          {/* =================================================
              FORGOT PASSWORD SUCCESS
          ================================================= */}

          {forgotPasswordSuccess && (
            <Alert
              severity="success"
              variant="outlined"
              sx={{
                mb: 2,

                borderRadius: "10px",

                fontSize: "12px",
              }}
            >
              {forgotPasswordSuccess}
            </Alert>
          )}

          {/* =================================================
              STEP 1 - EMPLOYEE ID + EMAIL
          ================================================= */}

          {step === 1 && (
            <>
              <TextField
                label="Employee ID"
                fullWidth
                value={fpEmpId}
                onChange={(event) => {
                  setFpEmpId(event.target.value);

                  setForgotPasswordError("");
                }}
                className={styles.fpInput}
                sx={{
                  mb: 2,
                }}
                variant="outlined"
                disabled={otpLoading}
                autoComplete="username"
              />

              <TextField
                label="Email Address"
                type="email"
                fullWidth
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);

                  setForgotPasswordError("");
                }}
                className={styles.fpInput}
                sx={{
                  mb: 3,
                }}
                variant="outlined"
                disabled={otpLoading}
                autoComplete="email"
              />

              <Button
                fullWidth
                variant="contained"
                disabled={
                  otpLoading || !String(fpEmpId).trim() || !String(email).trim()
                }
                className={styles.fpButton}
                onClick={handleSendOtp}
              >
                {otpLoading ? (
                  <>
                    <CircularProgress
                      size={16}
                      color="inherit"
                      sx={{
                        mr: 1,
                      }}
                    />
                    Sending…
                  </>
                ) : (
                  "Send OTP"
                )}
              </Button>
            </>
          )}

          {/* =================================================
              STEP 2 - VERIFY OTP
          ================================================= */}

          {step === 2 && (
            <>
              <TextField
                label="Enter OTP"
                fullWidth
                value={otp}
                onChange={(event) => {
                  const value = event.target.value.replace(/\D/g, "");

                  setOtp(value.slice(0, 6));

                  setForgotPasswordError("");
                }}
                className={styles.fpInput}
                inputProps={{
                  maxLength: 6,

                  inputMode: "numeric",

                  pattern: "[0-9]*",
                }}
                sx={{
                  mb: 2.5,
                }}
                variant="outlined"
                disabled={otpLoading}
              />

              <Button
                fullWidth
                variant="contained"
                disabled={otpLoading || !String(otp).trim()}
                className={styles.fpButton}
                sx={{
                  mb: 1,
                }}
                onClick={handleVerifyOtp}
              >
                {otpLoading ? (
                  <>
                    <CircularProgress
                      size={16}
                      color="inherit"
                      sx={{
                        mr: 1,
                      }}
                    />
                    Verifying…
                  </>
                ) : (
                  "Verify OTP"
                )}
              </Button>

              <Button
                fullWidth
                variant="text"
                disabled={timer > 0 || otpLoading}
                className={styles.fpResend}
                onClick={handleResendOtp}
              >
                {timer > 0 ? `Resend in ${timer}s` : "Resend OTP"}
              </Button>
            </>
          )}

          {/* =================================================
              STEP 3 - NEW PASSWORD
          ================================================= */}

          {step === 3 && (
            <>
              <TextField
                label="New Password"
                type={showNewPw ? "text" : "password"}
                fullWidth
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);

                  setForgotPasswordError("");
                }}
                onKeyDown={handleCapsLock(setCapsLockNewPw)}
                onKeyUp={handleCapsLock(setCapsLockNewPw)}
                onBlur={() => setCapsLockNewPw(false)}
                className={styles.fpInput}
                sx={{
                  mb: 1,
                }}
                variant="outlined"
                disabled={otpLoading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        onClick={() => setShowNewPw((current) => !current)}
                        edge="end"
                        size="small"
                        className={styles.passwordToggle}
                        disabled={otpLoading}
                        aria-label={
                          showNewPw ? "Hide new password" : "Show new password"
                        }
                      >
                        <EyeIcon off={showNewPw} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {capsLockNewPw && (
                <Typography className={styles.capsWarningModal}>
                  Caps Lock is on
                </Typography>
              )}

              {/* =============================================
                  PASSWORD STRENGTH BAR
              ============================================= */}

              <div
                className={styles.strengthBar}
                style={{
                  width: strengthWidth,

                  background: strengthColor,

                  maxWidth: "100%",
                }}
              />

              {newPassword && (
                <Typography
                  sx={{
                    fontSize: "12px",

                    mb: 2,

                    color: strengthColor,

                    fontFamily: "'DM Sans', system-ui, sans-serif",

                    fontWeight: 500,
                  }}
                >
                  Strength: {passwordStrength}
                </Typography>
              )}

              {/* =============================================
                  CONFIRM PASSWORD
              ============================================= */}

              <TextField
                label="Confirm New Password"
                type={showConfirmPw ? "text" : "password"}
                fullWidth
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);

                  setForgotPasswordError("");
                }}
                onKeyDown={handleCapsLock(setCapsLockConfirmPw)}
                onKeyUp={handleCapsLock(setCapsLockConfirmPw)}
                onBlur={() => setCapsLockConfirmPw(false)}
                error={Boolean(
                  confirmPassword && newPassword !== confirmPassword,
                )}
                helperText={
                  confirmPassword && newPassword !== confirmPassword
                    ? "Passwords do not match"
                    : ""
                }
                className={styles.fpInput}
                sx={{
                  mb: 3,
                }}
                variant="outlined"
                disabled={otpLoading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        onClick={() => setShowConfirmPw((current) => !current)}
                        edge="end"
                        size="small"
                        className={styles.passwordToggle}
                        disabled={otpLoading}
                        aria-label={
                          showConfirmPw
                            ? "Hide confirm password"
                            : "Show confirm password"
                        }
                      >
                        <EyeIcon off={showConfirmPw} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {capsLockConfirmPw && (
                <Typography className={styles.capsWarningModal}>
                  Caps Lock is on
                </Typography>
              )}

              {/* =============================================
                  RESET PASSWORD BUTTON
              ============================================= */}

              <Button
                fullWidth
                variant="contained"
                disabled={!isPasswordValid || otpLoading}
                className={styles.fpButton}
                onClick={handleResetPassword}
              >
                {otpLoading ? (
                  <>
                    <CircularProgress
                      size={16}
                      color="inherit"
                      sx={{
                        mr: 1,
                      }}
                    />
                    Resetting…
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
